"use client";

import { useState, useEffect, useCallback } from "react";
import { fetchLiveEventSnapshots } from "./liveEventsClient";
import { toLiveMatchData, type LiveMatchData } from "./snapshots";
import type { FUSportsMatchWithNativeId } from "@/utilities/data/types";

export type { LiveMatchData } from "./snapshots";

/** Polling interval for live match updates (30 seconds) */
const LIVE_MATCH_POLLING_INTERVAL_MS = 30000;

export interface UseLiveMatchDataOptions {
    /** The match to poll live data for */
    match: FUSportsMatchWithNativeId;
    /** Whether to enable polling (default: true) */
    enablePolling?: boolean;
    /** Custom polling interval in ms (default: 30000) */
    pollingIntervalMs?: number;
}

export interface UseLiveMatchDataResult {
    /** Current live match data */
    liveData: LiveMatchData;
    /** Whether the match is currently live */
    isLive: boolean;
    /** Current score */
    score: {
        competitorOne: string | null;
        competitorTwo: string | null;
    };
    /** Current match minute */
    matchMinute: string;
    /** Manually trigger a data refresh */
    refetch: () => Promise<void>;
}

/**
 * Polls `GET /api/live/events` for one match. The initial state comes from
 * the server-rendered match, so the first request only happens after the
 * first interval.
 */
export function useLiveMatchData({
    match,
    enablePolling = true,
    pollingIntervalMs = LIVE_MATCH_POLLING_INTERVAL_MS,
}: UseLiveMatchDataOptions): UseLiveMatchDataResult {
    const [liveData, setLiveData] = useState<LiveMatchData>({
        scoreCompetitorOne: match.score?.competitorOne ?? null,
        scoreCompetitorTwo: match.score?.competitorTwo ?? null,
        currentMinute: match.timing?.currentMinute?.toString() ?? null,
        statusType: match.status?.type ?? null,
        statusCode: (match.status?.code as string) ?? null,
    });

    const fetchLiveData = useCallback(async () => {
        const matchId = match.fansUnitedMatchId || match.id;
        if (!matchId) return;
        const [snapshot] = await fetchLiveEventSnapshots([matchId]);
        if (snapshot) setLiveData(toLiveMatchData(snapshot));
    }, [match.fansUnitedMatchId, match.id]);

    useEffect(() => {
        if (!enablePolling) return;
        const intervalId = setInterval(() => {
            void fetchLiveData();
        }, pollingIntervalMs);
        return () => clearInterval(intervalId);
    }, [fetchLiveData, enablePolling, pollingIntervalMs]);

    const isLive = liveData.statusType === "LIVE" || liveData.statusType === "IN_PROGRESS";

    return {
        liveData,
        isLive,
        score: { competitorOne: liveData.scoreCompetitorOne, competitorTwo: liveData.scoreCompetitorTwo },
        matchMinute: liveData.currentMinute || "",
        refetch: fetchLiveData,
    };
}

export { LIVE_MATCH_POLLING_INTERVAL_MS };
